import { toRustString } from "../rustString.js";
const rustReqwest = {
  target: "rust",
  client: "reqwest",
  title: "reqwest",
  generate(request, options) {
    const normalizedRequest = normalizeRequest(request);
    const queryString = buildQueryString(normalizedRequest.queryString);
    const url = buildUrl(normalizedRequest.url, queryString);
    const headers = processHeaders(normalizedRequest);
    const chainedCalls = [];
    const authCall = createAuthCall(options?.auth);
    if (authCall) {
      chainedCalls.push(authCall);
    }
    chainedCalls.push(...createHeaderCalls(headers));
    const bodyCall = createBodyCall(normalizedRequest.postData);
    if (bodyCall) {
      chainedCalls.push(bodyCall);
    }
    return buildRustCode(url, normalizedRequest.method, chainedCalls);
  }
};
const indent = (level, text) => {
  const spaces = " ".repeat(level * 4);
  return `${spaces}${text}`;
};
const createChainedCall = (method, ...args) => {
  return indent(1, `.${method}(${args.join(", ")})`);
};
const createMultipartPart = (param) => {
  if (param.fileName) {
    return [
      indent(2, `let part = reqwest::multipart::Part::text(${toRustString(param.value || "")})`),
      indent(3, `.file_name(${toRustString(param.fileName)});`),
      indent(2, `form = form.part(${toRustString(param.name)}, part);`)
    ].join("\n");
  }
  return indent(2, `form = form.text(${toRustString(param.name)}, ${toRustString(param.value || "")});`);
};
const formatJsonForRust = (jsonText) => {
  try {
    const jsonData = JSON.parse(jsonText);
    const prettyJson = JSON.stringify(jsonData, null, 4);
    const lines = prettyJson.split("\n");
    const rustLines = lines.map((line, index) => {
      if (index === 0) {
        return line;
      }
      if (index === lines.length - 1) {
        return indent(1, line);
      }
      return indent(1, line);
    });
    return rustLines.join("\n");
  } catch {
    return jsonText;
  }
};
const normalizeRequest = (request) => {
  return {
    ...request,
    method: (request.method || "GET").toUpperCase()
  };
};
const buildQueryString = (queryParams) => {
  if (!queryParams?.length) {
    return "";
  }
  const queryPairs = queryParams.map((param) => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`);
  return `?${queryPairs.join("&")}`;
};
const buildUrl = (baseUrl, queryString) => {
  return `${baseUrl}${queryString}`;
};
const processHeaders = (request) => {
  const headers = {};
  if (request.headers) {
    for (const header of request.headers) {
      if (header.value && !/[; ]/.test(header.name)) {
        headers[header.name] = header.value;
      }
    }
  }
  if (request.cookies?.length > 0) {
    const cookieString = request.cookies.map((cookie) => `${encodeURIComponent(cookie.name)}=${encodeURIComponent(cookie.value)}`).join("; ");
    headers["Cookie"] = cookieString;
  }
  return headers;
};
const createAuthCall = (auth) => {
  if (!auth?.username || !auth?.password) {
    return null;
  }
  return createChainedCall("basic_auth", toRustString(auth.username), toRustString(auth.password));
};
const createHeaderCalls = (headers) => {
  return Object.entries(headers).map(
    ([key, value]) => createChainedCall("header", toRustString(key), toRustString(value))
  );
};
const createBodyCall = (postData) => {
  if (!postData) {
    return null;
  }
  const { mimeType, text, params } = postData;
  switch (mimeType) {
    case "application/json": {
      const formattedJson = formatJsonForRust(text);
      return createChainedCall("json", `&serde_json::json!(${formattedJson})`);
    }
    case "application/x-www-form-urlencoded": {
      const formData = params?.map((param) => `(${toRustString(param.name)}, ${toRustString(param.value || "")})`).join(", ") || "";
      return createChainedCall("form", `&[${formData}]`);
    }
    case "multipart/form-data": {
      const formParts = params?.map(createMultipartPart).join("\n") || "";
      const multipartBlock = [
        ".multipart({",
        indent(2, "let mut form = reqwest::multipart::Form::new();"),
        formParts,
        indent(3, "form"),
        indent(2, "})")
      ].join("\n");
      return indent(1, multipartBlock);
    }
    default:
      return createChainedCall("body", toRustString(text || ""));
  }
};
const buildRustCode = (url, method, chainedCalls) => {
  const code = ["let client = reqwest::Client::new();", ""];
  if (chainedCalls.length > 0) {
    code.push("let request = client");
    code.push(indent(1, `.${method.toLowerCase()}(${toRustString(url)})`));
    code.push(...chainedCalls);
  } else {
    code.push(`let request = client.${method.toLowerCase()}(${toRustString(url)})`);
  }
  const lastPart = code[code.length - 1];
  code[code.length - 1] = lastPart + ";";
  code.push("");
  code.push("let response = request.send().await?;");
  return code.join("\n");
};
export {
  rustReqwest
};
//# sourceMappingURL=reqwest.js.map
